<template>
  <b-form class="registry-form">
    <b-row class="sticky-header pt-1">
      <b-col
        class="d-flex align-items-center"
        cols="auto"
      >
        <span
          qa="case-form-case-header-h5"
          class="ap-title ap-title__main"
        >
          {{ $t('registry.cases.caseData') }}
        </span>
      </b-col>
      <b-col>
        <div class="text-right d-flex justify-content-end align-items-center">
          <div class="switch-btn d-flex align-items-center mr-2">
            <small class="mr-2">{{ $t('grossing.content.inputFix') }}</small>
            <input
              id="toggleSwitchCase"
              v-model="fixFields"
              type="checkbox"
              class="offscreen"
              aria-hidden="true"
            >
            <label
              :qa="`case-form-clone-switch-${fixFields}-label`"
              for="toggleSwitchCase"
              class="switch mb-0 mr-2"
            />
          </div>
          <b-button
            v-if="$_canPrintCaseLabels()"
            v-tooltip="$t('registry.btn.printLabelCase')"
            qa="case-form-print-case-label-button"
            :disabled="!$_canBeCaseLabelsPrinted(caseData)"
            class="btn__icon btn__icon--primary px-2"
            @click="$emit('printCaseLabel')"
          >
            <i
              class="far fa-ticket-alt"
              aria-hidden="true"
            />
          </b-button>
          <b-button
            v-if="isEditMode"
            id="pop-estudio"
            qa="case-form-ellipsis-button"
            class="btn__icon btn__icon--primary pl-2 pr-1"
          >
            <i
              class="fas fa-ellipsis-v"
              aria-hidden="true"
            />
          </b-button>
          <b-popover
            v-if="isEditMode"
            target="pop-estudio"
            placement="bottom"
            triggers="click blur"
          >
            <div class="ap-popover">
              <ul>
                <li
                  v-if="$_canCreateIncident()"
                >
                  <b-button
                    qa="case-form-add-incident-button"
                    :disabled="!$_canBeIncidentCreatedToCase(caseData)"
                    @click="$emit('openIncidentFormModal', {coreEntity: 'CASE', entitiesIds: {casId: casId}})"
                  >
                    {{ $t('registry.requestGrid.contextMenu.registerIncident') }}
                  </b-button>
                </li>
                <li
                  v-if="$_canAttachDocumentToCase()"
                >
                  <b-button
                    qa="case-form-add-attachment-button"
                    :disabled="!$_canBeDocumentAttachedToNotValidatedCase(caseData)"
                    @click="showCaseAttachmentModal"
                  >
                    {{ $t('registry.btn.addAttach') }}
                  </b-button>
                </li>
                <li>
                  <b-button
                    qa="case-form-show-in-global-locator-button"
                    @click="showInGlobalLocator(caseData.casCasenumber)"
                  >
                    {{ $t('global.showInGlobalLocator') }}
                  </b-button>
                </li>
              </ul>
            </div>
          </b-popover>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="form-box">
        <div class="structure px-3">
          <b-row class="mt-1 mb-2">
            <b-col
              xl="2"
              lg="3"
              md="4"
              class="mb-2"
              :class="{'col-md-6 col-lg-4':showPreviousCases && !sidebarStateContract}"
            >
              <div class="form-label">
                <label :class="{ requiredlabel: (showErrors && isEmpty(formData.caseType)) }">
                  {{ $t('registry.cases.caseType') }} *
                  <vt-clone-toggle
                    v-model="internalSelectedFields"
                    :qa="'case-form-case-type-clone'"
                    field="caseType"
                    :disabled="!fixFields"
                  />
                </label>
              </div>
              <vt-combo-select
                id="caseType"
                ref="caseTypeCombo"
                v-model="formData.caseType"
                qa="case-form-case-type-select"
                :disabled="disabledCaseTypeCombo"
                track-by="id"
                :option-loader="getCaseTypes"
                label="descriptionInLaboratory"
                :allow-empty="false"
                data-vv-name="caseType"
                :class="{ requiredform: (showErrors && isEmpty(formData.caseType)) }"
                :focus="!casId"
              />
            </b-col>
            <b-col
              xl="2"
              lg="3"
              md="4"
              class="mb-2"
              :class="{'col-md-6 col-lg-4':showPreviousCases && !sidebarStateContract}"
            >
              <div class="form-label">
                <label>
                  {{ $t('global.regime') }}
                  <vt-clone-toggle
                    v-model="internalSelectedFields"
                    :qa="'case-form-regime-clone'"
                    field="regime"
                    :disabled="!fixFields"
                  />
                </label>
              </div>
              <vt-combo-select
                id="regime"
                v-model="formData.regime"
                qa="case-form-regime-select"
                :disabled="isReadOnly"
                class="nowrap"
                track-by="id"
                :option-loader="getRegimes"
                label="name"
                @select="$forceUpdate()"
                @remove="$forceUpdate()"
              />
            </b-col>
            <b-col
              xl="2"
              lg="2"
              md="4"
              class="mb-2"
              :class="{'col-lg-4':showPreviousCases && !sidebarStateContract}"
            >
              <div class="form-label">
                <label
                  :class="{ requiredlabel: (showErrors && !formData.priority) }"
                >
                  {{ $t('global.priority') }} *
                  <vt-clone-toggle
                    v-model="internalSelectedFields"
                    :qa="'case-form-priority-clone'"
                    field="priority"
                    :disabled="!fixFields"
                  />
                </label>
              </div>
              <vt-combo-select
                id="priority"
                v-model="formData.priority"
                qa="case-form-priority-select"
                :disabled="isReadOnly"
                track-by="id"
                :options="priorities"
                :allow-empty="false"
                label="name"
                data-vv-name="priority"
                :class="{ requiredform: (showErrors && isEmpty(formData.priority)) }"
              />
            </b-col>
            <b-col
              xl="1"
              lg="2"
              md="3"
              sm="6"
              class="mb-2"
              :class="{'col-md-4 col-lg-4':showPreviousCases && !sidebarStateContract}"
            >
              <div class="form-label">
                <label>{{ $t('global.age') }}</label>
              </div>
              <vt-input
                qa="case-form-age-input"
                disabled
                class="form-control-sm"
                :value="patientAge"
              />
            </b-col>
            <b-col
              xl="2"
              lg="2"
              md="3"
              sm="6"
              class="mb-2"
              :class="{'col-md-4 col-lg-4':showPreviousCases && !sidebarStateContract}"
            >
              <div class="form-label">
                <label>
                  {{ $t('global.episode') }}
                  <vt-clone-toggle
                    v-model="internalSelectedFields"
                    :qa="'case-form-episode-clone'"
                    field="casEpisode"
                    :disabled="!fixFields"
                  />
                </label>
              </div>
              <vt-input
                v-model="formData.casEpisode"
                qa="case-form-episode-input"
                class="form-control-sm"
                :disabled="isReadOnly"
                :name="$t('global.episode')"
              />
            </b-col>
            <b-col
              xl="3"
              lg="4"
              md="6"
              class="mb-2"
            >
              <div class="form-label">
                <label>
                  {{ $t('grid.detail.patName') }}
                  <i
                    v-if="formData.automaticPathologist"
                    v-tooltip="$tc('global.automaticAssignment')"
                    class="fal fa-magic ml-1"
                    aria-hidden="true"
                  />
                  <vt-clone-toggle
                    v-model="internalSelectedFields"
                    :qa="'case-form-pathologist-clone'"
                    field="pathologist"
                    :disabled="!fixFields"
                  />
                </label>
              </div>
              <vt-combo-select
                id="pathologist"
                qa="case-form-pathologist-select"
                :disabled="disabledPathologistCombo"
                :value="formData.pathologist"
                track-by="id"
                :option-loader="getPersonnelPathologist"
                label="fullName"
                name="pathologist"
                @input="onPathologistComboInput"
              />
            </b-col>
            <b-col
              xl="3"
              lg="4"
              md="6"
              class="mb-2"
            >
              <div class="form-label">
                <label>
                  {{ $t('registry.cases.resident') }}
                  <i
                    v-if="formData.automaticResident"
                    v-tooltip="$tc('global.automaticAssignment')"
                    class="fal fa-magic ml-1"
                    aria-hidden="true"
                  />
                  <vt-clone-toggle
                    v-model="internalSelectedFields"
                    :qa="'case-form-resident-clone'"
                    field="resident"
                    :disabled="!fixFields"
                  />
                </label>
              </div>
              <vt-combo-select
                id="resident"
                v-model="formData.resident"
                qa="case-form-resident-select"
                :disabled="isReadOnly"
                track-by="id"
                :option-loader="getPersonnelResident"
                label="fullName"
                name="resident"
              />
            </b-col>
            <b-col
              xl="2"
              lg="2"
              md="3"
              class="mb-2"
              :class="{'col-md-6 col-lg-4':showPreviousCases && !sidebarStateContract}"
            >
              <div class="form-label">
                <label>{{ $t('grid.detail.requestId') }}</label>
              </div>
              <vt-input
                v-model="formData.requestLabel"
                qa="case-form-request-input"
                class="form-control-sm"
                disabled
              />
            </b-col>
            <b-col
              xl="2"
              lg="2"
              md="3"
              class="mb-2"
              :class="{'col-md-6 col-lg-4':showPreviousCases && !sidebarStateContract}"
            >
              <div class="form-label">
                <label :class="{'label-truncate':showPreviousCases}">{{ $t('global.externalReference') }}</label>
              </div>
              <vt-input
                v-model="formData.externalReference"
                qa="case-form-external-reference-input"
                class="form-control-sm"
                :disabled="isReadOnly"
              />
            </b-col>
            <b-col
              xl="7"
              lg="7"
              md="6"
              class="mb-2"
              :class="{'col-md-12':showPreviousCases && !sidebarStateContract}"
            >
              <div class="form-label">
                <label>{{ $t('global.relevantClinicalData') }}</label>
              </div>
              <b-form-textarea
                v-model="formData.clinicalData.text"
                qa="case-form-clinical-data-textarea-textarea"
                :qa-value="formData.clinicalData.text"
                class="text-box"
                size="sm"
                :disabled="isReadOnly"
              />
            </b-col>
            <b-col
              xl="5"
              lg="5"
              md="6"
              class="mb-2"
              :class="{'col-md-12':showPreviousCases && !sidebarStateContract}"
            >
              <div class="form-label">
                <label>{{ $t('global.observations') }}</label>
              </div>
              <b-form-textarea
                v-model="formData.notes.text"
                qa="case-form-observations-textarea-textarea"
                :qa-value="formData.notes.text"
                class="text-box"
                size="sm"
                :disabled="isReadOnly"
              />
            </b-col>
          </b-row>
          <div class="mt-4">
            <span class="ap-title ap-title__main">
              {{ $t('registry.cases.registryData') }}
            </span>
          </div>
          <b-row class="mt-3 mb-2">
            <b-col
              xl="3"
              lg="3"
              md="3"
              sm="6"
              class="mb-sm-2 mb-lg-0"
              :class="{'col-md-6':showPreviousCases && !sidebarStateContract}"
            >
              <div class="form-label">
                <label :class="{ requiredlabel: (showErrors && !formData.casRequestDate) }">
                  {{ $t('grid.detail.casRequestDate') }} *
                </label>
              </div>
              <vt-date-picker
                qa="case-form-request-date"
                :disabled="disabledRequestDate"
                lang="es"
                :model="formData.casRequestDate"
                :class="{ requiredcalendar: (showErrors && !formData.casRequestDate) }"
                :not-after="requestDateNotAfter"
                @update="formData.casRequestDate = $event"
              />
            </b-col>
            <b-col
              xl="3"
              lg="3"
              md="3"
              sm="6"
              class="mb-sm-2 mb-lg-0"
              :class="{'col-md-6':showPreviousCases && !sidebarStateContract}"
            >
              <div class="form-label">
                <label>{{ $t('grid.detail.casRegistrationDate') }}</label>
              </div>
              <vt-date-picker
                qa="case-form-registration-date"
                disabled
                :model="formData.casRegistrationDate"
              />
            </b-col>
            <b-col
              xl="3"
              lg="3"
              md="6"
              class="mb-sm-2 mb-lg-0"
            >
              <div class="form-label">
                <label>
                  {{ $t('registry.cases.register') }}
                </label>
              </div>
              <vt-input
                id="registry"
                qa="case-form-register-input"
                class="form-control-sm"
                disabled
                name="registry"
                :value="registerPersonnel"
              />
            </b-col>
            <b-col
              xl="3"
              lg="3"
              md="6"
              class="mb-sm-2 mb-lg-0"
            >
              <div class="form-label">
                <label>
                  {{ $t('registry.cases.administrative') }}
                  <vt-clone-toggle
                    v-model="internalSelectedFields"
                    :qa="'case-form-administrative-clone'"
                    field="administrative"
                    :disabled="!fixFields"
                  />
                </label>
              </div>
              <vt-combo-select
                id="administrative"
                v-model="formData.administrative"
                qa="case-form-administrative-select"
                :disabled="isReadOnly"
                :open-direction="'top'"
                track-by="id"
                :option-loader="getPersonnelAdministrative"
                label="fullName"
                name="administrative"
              />
            </b-col>
          </b-row>

          <b-row>
            <b-col
              xl="6"
              lg="12"
            >
              <b-row class="pt-2">
                <b-col
                  cols="auto"
                  class="d-flex align-items-center"
                >
                  <span class="ap-title ap-title__divider">{{ $t('global.requester') }}</span>
                  <b-form-checkbox
                    v-if="!casId"
                    id="check-destination"
                    v-model="differentDestination"
                    :qa="`case-form-different-destination-checkbox-${differentDestination}-div`"
                    class="ml-3"
                    :disabled="isReadOnly"
                    :open-direction="'top'"
                    name="check-destination"
                    :value="true"
                    :unchecked-value="false"
                  />
                  <small v-if="!casId">{{ $t('registry.cases.differentDestination') }}</small>
                </b-col>
                <b-col>
                  <hr>
                </b-col>
              </b-row>
              <b-row class="mt-0 mb-2">
                <b-col
                  sm="6"
                  md
                  class="mb-sm-2 mb-lg-0"
                  :class="{'col-sm-12 col-md-6 col-lg-4':showPreviousCases && !sidebarStateContract}"
                >
                  <div class="form-label">
                    <label>
                      {{ $t('grid.detail.cenPhysName') }} *
                      <vt-clone-toggle
                        v-model="internalSelectedFields"
                        :qa="'case-form-physician-center-clone'"
                        field="physicianCenter"
                        :disabled="!fixFields"
                      />
                    </label>
                  </div>
                  <vt-combo-select
                    id="physicianCenter"
                    v-model="selectedPhysicianCenter"
                    qa="case-form-physician-center-select"
                    :disabled="isReadOnly"
                    track-by="id"
                    :option-loader="loadCenters"
                    label="name"
                    :class="{ requiredform: (showErrors && isEmpty(selectedPhysicianCenter)) }"
                    :open-direction="'top'"
                    @input="selectPhysicianCenter"
                    @remove="removePhysicianCenter"
                  />
                </b-col>
                <b-col
                  class="mb-sm-2 mb-lg-0"
                  :class="{'col-sm-12 col-md-6 col-lg-4':showPreviousCases && !sidebarStateContract}"
                >
                  <div class="form-label">
                    <label>
                      {{ $t('registry.cases.unit') }} *
                      <vt-clone-toggle
                        v-model="internalSelectedFields"
                        :qa="'case-form-physician-department-clone'"
                        field="physicianDepartment"
                        :disabled="!fixFields"
                      />
                    </label>
                  </div>
                  <vt-combo-select
                    id="physicianDepartment"
                    :key="selectedPhysicianCenterId"
                    v-model="formData.physicianDepartment"
                    qa="case-form-physician-department-select"
                    :open-direction="'top'"
                    :disabled="isReadOnly || isEmpty(selectedPhysicianCenter)"
                    track-by="id"
                    :option-loader="loadPhysicianDepartments"
                    label="name"
                    :class="{ requiredform: (showErrors && !formData.physicianDepartment && !isEmpty(selectedPhysicianCenter)) }"
                    @input="selectPhysicianDepartment"
                  />
                </b-col>
                <b-col
                  class="mb-sm-2 mb-lg-0"
                  :class="{'col-sm-12 col-md-6 col-lg-4':showPreviousCases && !sidebarStateContract}"
                >
                  <div class="form-label">
                    <label>
                      {{ $t('registry.cases.professional') }}
                      <vt-clone-toggle
                        v-model="internalSelectedFields"
                        :qa="'case-form-physician-petitioner-clone'"
                        field="physicianPetitioner"
                        :disabled="!fixFields"
                      />
                    </label>
                  </div>
                  <vt-combo-select
                    id="requestFormClinicalPetitioner"
                    v-model="formData.physicianPetitioner"
                    qa="case-form-physician-petitioner-select"
                    :open-direction="'top'"
                    :disabled="isReadOnly"
                    track-by="id"
                    :option-loader="loadPetitioners"
                    ajax-search
                    label="fullName"
                    :internal-search="false"
                    :value-ajax="formData.physicianPetitioner"
                    @input="customLabelProfessional($event)"
                  />
                </b-col>
              </b-row>
            </b-col>
            <b-col>
              <b-row
                class="pt-2"
              >
                <b-col
                  cols="auto"
                  class="d-flex align-items-center"
                >
                  <span class="ap-title ap-title__divider">{{ $tc('global.insurance', 1) }}</span>
                </b-col>
                <b-col>
                  <hr>
                </b-col>
              </b-row>
              <b-row
                class="mt-0 mb-2"
              >
                <b-col
                  sm="6"
                  md
                  class="mb-sm-2 mb-lg-0"
                  :class="{'col-sm-12 col-md-6 col-lg-4':showPreviousCases && !sidebarStateContract}"
                >
                  <div class="form-label">
                    <label>
                      {{ $tc('global.insurance', 1) }}
                      <vt-clone-toggle
                        v-model="internalSelectedFields"
                        field="insurer"
                        :disabled="!fixFields"
                        :qa="'case-form-insurance-clone'"
                      />
                    </label>
                  </div>
                  <vt-combo-select
                    v-model="formData.insurer"
                    label="name"
                    qa="case-form-insurance-select"
                    :option-loader="findAllActiveInsurers"
                    :disabled="isReadOnly"
                  />
                </b-col>
                <b-col
                  class="mb-sm-2 mb-lg-0"
                  :class="{'col-sm-12 col-md-6 col-lg-4':showPreviousCases && !sidebarStateContract}"
                >
                  <div class="form-label">
                    <label>
                      {{ $tc('global.insuranceDocument') }}
                      <vt-clone-toggle
                        v-model="internalSelectedFields"
                        field="referralNumber"
                        :disabled="!fixFields"
                        :qa="'case-form-insurance-document-clone'"
                      />
                    </label>
                  </div>
                  <vt-input
                    v-model="formData.referralNumber"
                    :disabled="isReadOnly"
                    size="sm"
                  />
                </b-col>
                <b-col
                  class="mb-sm-2 mb-lg-0"
                  :class="{'col-sm-12 col-md-6 col-lg-4':showPreviousCases && !sidebarStateContract}"
                >
                  <div class="form-label">
                    <label>
                      {{ $t('global.insurancePolicy') }}
                      <vt-clone-toggle
                        v-model="internalSelectedFields"
                        field="insuranceNumber"
                        :disabled="!fixFields"
                        :qa="'case-form-insurance-policy-clone'"
                      />
                    </label>
                  </div>
                  <vt-input
                    v-model="formData.insuranceNumber"
                    :disabled="isReadOnly"
                    size="sm"
                  />
                </b-col>
              </b-row>
            </b-col>
            <b-col
              xl="6"
              lg="12"
            >
              <b-row
                v-if="differentDestination || casId"
                class="pt-2"
              >
                <b-col
                  cols="auto"
                  class="d-flex align-items-center"
                >
                  <span class="ap-title ap-title__divider">{{ $t('registry.cases.destination') }}</span>
                </b-col>
                <b-col>
                  <hr>
                </b-col>
              </b-row>
              <b-row
                v-if="differentDestination || casId"
                class="mt-0 mb-2"
              >
                <b-col
                  sm="6"
                  md
                  class="mb-sm-2 mb-lg-0"
                  :class="{'col-sm-12 col-md-6 col-lg-4':showPreviousCases && !sidebarStateContract}"
                >
                  <div class="form-label">
                    <label>
                      {{ $t('grid.detail.cenPhysName') }}
                      <vt-clone-toggle
                        v-model="internalSelectedFields"
                        :qa="'case-form-destination-center-clone'"
                        field="destinationCenter"
                        :disabled="!fixFields"
                      />
                    </label>
                  </div>
                  <vt-combo-select
                    v-model="selectedDestinationCenter"
                    qa="case-form-destination-center-select"
                    :disabled="isReadOnly"
                    :open-direction="'top'"
                    track-by="id"
                    :option-loader="loadCenters"
                    label="name"
                    @input="selectDestinationCenter"
                    @remove="removeDestinationCenter"
                  />
                </b-col>
                <b-col
                  class="mb-sm-2 mb-lg-0"
                  :class="{'col-sm-12 col-md-6 col-lg-4':showPreviousCases && !sidebarStateContract}"
                >
                  <div class="form-label">
                    <label>
                      {{ $t('registry.cases.unit') }}
                      <vt-clone-toggle
                        v-model="internalSelectedFields"
                        :qa="'case-form-destination-department-clone'"
                        field="destinationDepartment"
                        :disabled="!fixFields"
                      />
                    </label>
                  </div>
                  <vt-combo-select
                    id="destinationDepartment"
                    :key="selectedDestinationCenterId"
                    v-model="formData.destinationDepartment"
                    qa="case-form-destination-department-select"
                    :disabled="isReadOnly || isEmpty(selectedDestinationCenter)"
                    :open-direction="'top'"
                    track-by="id"
                    :option-loader="loadDestinationDepartments"
                    label="name"
                    @input="selectDestinationDepartment"
                  />
                </b-col>
                <b-col
                  class="mb-sm-2 mb-lg-0"
                  :class="{'col-sm-12 col-md-6 col-lg-4':showPreviousCases && !sidebarStateContract}"
                >
                  <div class="form-label">
                    <label>
                      {{ $t('global.receiver') }}
                      <vt-clone-toggle
                        v-model="internalSelectedFields"
                        :qa="'case-form-destination-petitioner-clone'"
                        field="casAttn"
                        :disabled="!fixFields"
                      />
                    </label>
                  </div>
                  <vt-input
                    v-model="formData.casAttn"
                    qa="case-form-destination-petitioner-input"
                    class="form-control-sm"
                    name="destinationPet"
                    :disabled="isReadOnly"
                  />
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
    <modal-wrapper ref="addFileModalWrapper">
      <add-file-modal
        id="addFileModal"
        ref="addFileModal"
        :attachment-entity-type="ATTACHMENT_ENTITY_TYPE"
        :entities-id-array="entitiesIdArray"
        @attachmentsAdded="onAttachmentsAdded"
      />
    </modal-wrapper>
  </b-form>
</template>
<script>
import studySidebarStore from '@/modules/RegistryModule/sideBar/case/registryCaseSideBarStore'
import CommonCaseTypeServices from '@/api/resources/common/commonCaseTypeServices'
import RegimeResource from '@/api/resources/RegimeResource'
import commonDepartmentService from '@/api/resources/common/commonDepartmentService'
import { personnelService } from '@/api/resources/personnelService'
import CenterResource from '@/api/resources/common/commonCenterService'
import moment from 'moment'
import sessionStore from '@/session/sessionStore'
import CaseServices from '@/modules/RegistryModule/services/registryCaseServices'
import petitionerResource from '@/api/resources/PetitionerResource'
import AddFileModal from '@/components/AddFileModal'
import { assign, cloneDeep, isEqual, pick } from 'lodash'
import caseClinicalDataService from '@/api/resources/clob/CaseClinicalDataService'
import caseNotesService from '@/api/resources/clob/CaseNotesService'
import customConfigResource from '@/api/resources/ui/CustomConfigResource'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/resources/attachment/attachmentResource'
import { CaseOperationGuardMixin } from '@/api/operations/mixins/guards/case-operation-guard.mixin'
import { IncidentOperationGuardMixin } from '@/api/operations/mixins/guards/incident-operation-guard.mixin'
import { CommonResponsibilityCodeEnum } from '@/api/services/common/responsibility/common-responsibility.dto'
import { commonInsurerService } from '@/api/services/common/insurer/common-insurer.service'
import { StoreUtils } from '@/shared/util/store-utils'
import { mountPatientAgeString } from '@/utils/fieldTransformations'
import { store } from '@/store/store'

const NEW_CASE_ROUTE_NAME = 'newCase'
const SIDEBAR_STATE = {
    EXPANDED: 'ExpandedSideBar',
    CONTRACT: 'StudyRequestCompactSideBar'
}

export default {
    name: 'CaseForm',
    components: {
        AddFileModal
    },
    mixins: [
        CaseOperationGuardMixin,
        IncidentOperationGuardMixin
    ],
    props: {
        caseData: { type: Object },
        patientInformation: { type: Object },
        printedLabel: { type: Boolean },
        showPreviousCases: { type: Boolean },
        caseExternalLabel: { type: String }
    },
    data() {
        return {
            caseLabel: '',
            selectedFields: [],
            fixFields: false,
            destinationPet: null,
            caseClone: null,
            showErrors: false,
            isNewSave: false,
            casId: null,
            formData: this.getFormData(),
            differentDestination: false,
            message: null,
            selectedPhysicianCenter: this.defaultPhysicianCenter,
            selectedPhysicianCenterId: this.defaultPhysicianCenterId,
            selectedDestinationCenter: null,
            selectedDestinationCenterId: null,
            caseAutomaticPathologist: null
        }
    },
    computed: {
        disabledPathologistCombo() {
            return this.isReadOnly || this.$_isCaseInAmendment(this.caseData?.status.code)
        },
        disabledCaseTypeCombo() {
            return this.casId ? !this.$_isCaseDraft(this.caseData?.status.code) : false
        },
        requestDateNotAfter() {
            if (this.formData.casRegistrationDate) {
                return new Date(this.formData.casRegistrationDate)
            }
            return new Date()
        },
        registerPersonnel() {
            return this.formData.registerPersonnel?.fullName
        },
        isReadOnly() {
            return !this.$_canSaveCase(this.casId) ||
                (!!this.casId && !this.$_isCaseEditable(this.caseData?.status.code))
        },
        disabledRequestDate() {
            return this.isReadOnly || this.$_isCaseInAmendment(this.caseData?.status.code)
        },
        isEditMode() {
            return !!this.casId
        },
        sidebarStateContract() {
            return studySidebarStore.state.sideBar.name === SIDEBAR_STATE.CONTRACT
        },
        entitiesIdArray() {
            if (this.caseData && this.caseData.casId) {
                return [this.caseData.laboratory.labId, this.caseData.casId]
            }
            return []
        },
        ATTACHMENT_ENTITY_TYPE() {
            return ATTACHMENT_ENTITY_TYPE.CASE
        },
        internalSelectedFields: {
            get() {
                if (this.fixFields) {
                    return this.selectedFields
                }
                return []
            },
            set(newValue) {
                this.selectedFields = newValue
                const selectedFieldsToSave = { fields: this.selectedFields }
                customConfigResource.save('uicomponent.registry.fixcaseattributes', selectedFieldsToSave)
                this.saveFixFieldsContent()
            }
        },
        patientAge() {
            return this.caseData && !this.$_isCaseDraft(this.caseData?.status.code)
                ? mountPatientAgeString(this.caseData.casPatAge)
                : this.$tc('globalLocator.labels.notAvailableData')
        },
        labId() {
            return store.state.labId
        },
        priorities() {
            return sessionStore.state.priorities.filter(priority => priority.active)
        },
        defaultPhysicianCenter() {
            return StoreUtils.selectedLaboratoryConfiguration?.center
        },
        defaultPhysicianCenterId() {
            return this.defaultPhysicianCenter?.id ?? null
        },
        caseType() {
            return this.formData.caseType
        }
    },
    watch: {
        caseType() {
            this.$emit('loadSampleTypes', this.formData.caseType?.id)
        },
        async fixFields(newValue) {
            store.commit('registry/updateFixFieldsToggle', newValue)
            if (newValue) {
                const customConfigFields = await customConfigResource.get('uicomponent.registry.fixcaseattributes')
                if (customConfigFields && customConfigFields.config.fields instanceof Array) {
                    this.selectedFields = customConfigFields.config.fields
                    this.saveFixFieldsContent()
                }
            }
        },
        formData: {
            handler() {
                if (this.fixFields && this.selectedFields.length) {
                    this.saveFixFieldsContent()
                }
            },
            deep: true
        },
        caseData: {
            handler(newCaseDataValue) {
                if (newCaseDataValue) {
                    this.initData(this.caseData)
                    this.caseClone = cloneDeep(this.formData)
                }
            },
            deep: true
        }
    },
    created() {
        this.formData = this.getFormData()
        this.selectedPhysicianCenter = this.defaultPhysicianCenter
        this.selectedPhysicianCenterId = this.defaultPhysicianCenterId
        this.selectedDestinationCenter = null
        this.selectedDestinationCenterId = null
        if (this.caseData) {
            this.initData(this.caseData)
        }
        this.caseClone = cloneDeep(this.formData)
        if (this.$route.name === NEW_CASE_ROUTE_NAME) {
            this.fixFields = store.getters['registry/fixFieldsToggle']
        }
        this.setFixFieldsContent()
    },
    methods: {
        async findAllActiveInsurers() {
            const response = await this.$_toCallSecure(commonInsurerService.findAllActiveInsurers())
            return response ?? []
        },
        showInGlobalLocator(label) {
            this.$eventHub.$emit('show-in-global-locator', label)
        },
        setFixFieldsContent() {
            if (this.fixFields && this.$route.name === NEW_CASE_ROUTE_NAME) {
                const fixFieldsContent = store.getters['registry/fixFieldsContent']
                assign(this.formData, fixFieldsContent)
                if (fixFieldsContent.casAttn) {
                    this.differentDestination = true
                }

                if (fixFieldsContent.physicianPetitioner) {
                    this.formData.physicianPetitioner = fixFieldsContent.physicianPetitioner
                }

                if (fixFieldsContent.physicianCenter) {
                    this.selectedPhysicianCenter = fixFieldsContent.physicianCenter
                    this.selectedPhysicianCenterId = fixFieldsContent.physicianCenter.id
                }

                if (fixFieldsContent.destinationCenter) {
                    this.selectedDestinationCenter = fixFieldsContent.destinationCenter
                    this.selectedDestinationCenterId = fixFieldsContent.destinationCenter.id
                    this.differentDestination = true
                }

                if (fixFieldsContent.physicianDepartment) {
                    this.selectedPhysicianCenter = this.formData.physicianDepartment.center
                    this.selectedPhysicianCenterId = this.formData.physicianDepartment.center.id
                }

                if (fixFieldsContent.destinationDepartment) {
                    this.selectedDestinationCenter = this.formData.destinationDepartment.center
                    this.selectedDestinationCenterId = this.formData.destinationDepartment.center.id
                    this.differentDestination = true
                }
            }
        },
        saveFixFieldsContent() {
            let selectedFieldsContent = pick(this.formData, this.selectedFields)
            if (this.selectedFields.includes('physicianCenter')) {
                selectedFieldsContent.physicianCenter = this.selectedPhysicianCenter
            }
            if (this.selectedFields.includes('destinationCenter')) {
                selectedFieldsContent.destinationCenter = this.selectedDestinationCenter
            }
            store.commit('registry/updateFixFieldsContent', selectedFieldsContent)
        },
        showCaseAttachmentModal() {
            this.$refs.addFileModalWrapper.show()
        },
        async onAttachmentsAdded() {
            this.$emit('case-attachment-added')
        },
        initData(caseData) {
            this.caseLabel = caseData.casCasenumber
            this.casId = caseData.casId
            this.selectedPhysicianCenter = caseData.physicianDepartment ? caseData.physicianDepartment.center : null
            this.selectedPhysicianCenterId = caseData.physicianDepartment ? caseData.physicianDepartment.center.id : null
            this.selectedDestinationCenter = caseData.destinationDepartment ? caseData.destinationDepartment.center : null
            this.selectedDestinationCenterId = caseData.destinationDepartment ? caseData.destinationDepartment.center.id : null
            this.caseAutomaticPathologist = caseData.automaticPathologist ? caseData.pathologist : null
            this.formData = cloneDeep(caseData)
        },
        customLabelProfessional(data) {
            this.destinationPet = [data.name, data.middleName, data.surname1, data.surname2].filter(Boolean).join(' ')
        },
        getFormData() {
            return {
                administrative: null,
                associatedCaseCasId: null,
                casAttn: null,
                casEpisode: null,
                casRequestDate: moment().seconds(0).milliseconds(0),
                casRegistrationDate: null,
                caseType: null,
                clinicalData: { text: '' },
                destinationDepartment: null,
                notes: { text: '' },
                pathologist: null,
                patientPatId: null,
                physicianDepartment: null,
                physicianPetitioner: null,
                priority: null,
                regime: null,
                resident: null,
                caseOfReference: false,
                automaticPathologist: false,
                externalReference: '',
                insurer: null,
                referralNumber: null,
                insuranceNumber: null
            }
        },
        async getCaseTypes() {
            return CommonCaseTypeServices.getActiveCaseTypesByLaboratory(this.labId)
        },
        async getRegimes() {
            return RegimeResource.lightList()
        },
        async getPersonnelPathologist() {
            return personnelService.getPersonnelByLaboratoryAndResponsibility(this.labId, CommonResponsibilityCodeEnum.PATHOLOGIST)
        },
        async getPersonnelResident() {
            return personnelService.getPersonnelByLaboratoryAndResponsibility(this.labId, CommonResponsibilityCodeEnum.RESIDENT)
        },
        async getPersonnelAdministrative() {
            return personnelService.getPersonnelByLaboratoryAndResponsibility(this.labId, CommonResponsibilityCodeEnum.ADMINISTRATIVE)
        },
        async loadCenters() {
            return CenterResource.getActiveCenters()
        },
        async loadDepartments(center) {
            if (center) {
                return commonDepartmentService.findAllActiveByCenter(center.id)
            } else {
                return []
            }
        },
        async loadPetitioners(query) {
            const paginator = await petitionerResource.paginator({
                active: true,
                nameFragments: query,
                size: 200
            })
            return paginator.content
        },
        async registerCase() {
            let registered = false
            this.showErrors = false
            this.formData.casId = this.casId

            const response =
                await this.$_toCallSecure(
                    CaseServices.registerCase(this.labId, this.casId),
                    () => this.$_showErrorModal(this.$tc('registry.index.registerFormError')))
            if (response) {
                registered = true
                this.$emit('mapCaseData', response)
                this.initData(response)
                this.caseClone = cloneDeep(this.formData)
            }
            return registered
        },
        mapFormDataToRequest(formData) {
            return {
                administrativePerId: formData.administrative?.id ?? null,
                automaticPathologist: formData.automaticPathologist,
                casAttn: formData.casAttn,
                casEpisode: formData.casEpisode,
                casRequestDate: formData.casRequestDate,
                caseOfReference: formData.caseOfReference,
                caseTypeId: formData.caseType?.id,
                destinationDepartmentId: formData.destinationDepartment?.id ?? null,
                externalReference: formData.externalReference,
                label: this.printedLabel ? this.caseExternalLabel : this.caseLabel,
                isExternalLabel: this.printedLabel,
                laboratoryId: formData.laboratory?.labId ?? this.labId,
                originalConcurrencyUpdatedAt: formData.originalConcurrencyUpdatedAt,
                pathologistPerId: formData.pathologist?.id ?? null,
                physicianDepartmentId: formData.physicianDepartment?.id ?? null,
                physicianPetitionerPetId: formData.physicianPetitioner?.id ?? null,
                priorityId: formData.priority?.id ?? null,
                regimeId: formData.regime?.id ?? null,
                requestLabel: formData.requestLabel,
                residentPerId: formData.resident?.id ?? null,
                clinicalData: {
                    originalConcurrencyUpdatedAt: formData.originalConcurrencyUpdatedAt,
                    text: formData.clinicalData.text
                },
                notes: {
                    originalConcurrencyUpdatedAt: formData.originalConcurrencyUpdatedAt,
                    text: formData.notes.text
                },
                insurerId: formData.insurer?.id,
                referralNumber: formData.referralNumber,
                insuranceNumber: formData.insuranceNumber
            }
        },
        async saveForm() {
            this.showErrors = false
            if (this.casId) {
                if (await this.updateClobs()) {
                    return this.updateCase(false)
                }
            } else {
                if (!this.differentDestination) {
                    this.formData.destinationDepartment = this.formData.physicianDepartment
                    this.formData.casAttn = this.destinationPet
                }
                const request = this.mapFormDataToRequest(this.formData)
                request.patientPatId = this.patientInformation.patId

                const response =
                    await this.$_toCallSecure(
                        CaseServices.createCase(this.labId, request),
                        error => this.$_showErrorModalFromErrorResponse(error, this.$tc('registry.index.saveFormError')))
                if (response) {
                    this.isNewSave = true
                    this.initData(response)
                    this.caseClone = cloneDeep(this.formData)
                    this.$emit('mapCaseData', response)
                    this.$emit('formUpdated', response.casId)
                    return true
                }
            }
            return false
        },
        async updateClobs() {
            const successfulUpdate = await this.updateClinicalData() && await this.updateNotes()
            return successfulUpdate
        },
        async updateClinicalData() {
            if (this.formData.clinicalData.text === this.caseClone.clinicalData.text) {
                return true
            }
            let [err, response] = await this.toCall(caseClinicalDataService.update(this.labId, this.casId, this.formData.clinicalData))
            if (err) {
                if (err.response && err.response.data && err.response.data.code === this.$apiExceptions.CONCURRENCY_ERROR) {
                    this.updateLoading(false)
                    return this.processBasicConcurrencyConflict(this.$t('global.relevantClinicalData'), err, async () => {
                        this.formData.clinicalData.originalConcurrencyUpdatedAt = err.response.data.currentConcurrencyUpdatedAt
                        this.updateLoading(true)
                        return this.updateClinicalData()
                    })
                } else {
                    this.showErrorModalGlobal(this.$t('registry.index.errorSavingClinicalData'))
                    return false
                }
            }
            this.formData.clinicalData = response
            this.caseClone.clinicalData = response
            return true
        },
        async updateNotes() {
            if (this.formData.notes.text === this.caseClone.notes.text) {
                return true
            }
            let [err, response] = await this.toCall(caseNotesService.update(this.labId, this.casId, this.formData.notes))
            if (err) {
                if (err.response && err.response.data && err.response.data.code === this.$apiExceptions.CONCURRENCY_ERROR) {
                    this.updateLoading(false)
                    return this.processBasicConcurrencyConflict(this.$t('global.observations'), err, async () => {
                        this.formData.notes.originalConcurrencyUpdatedAt = err.response.data.currentConcurrencyUpdatedAt
                        this.updateLoading(true)
                        return this.updateNotes()
                    })
                } else {
                    this.showErrorModalGlobal(this.$t('registry.index.errorSavingNotes'))
                    return false
                }
            }
            this.formData.notes = response
            this.caseClone.notes = response
            return true
        },
        async updateCase(deleteSamplesIfIncompatible) {
            const request = this.mapFormDataToRequest(this.formData)
            let [err, response] = await this.toCall(CaseServices.updateCase(this.labId, this.casId, request, deleteSamplesIfIncompatible))
            if (err) {
                this.updateLoading(false)
                if (err.response.data.code === this.$apiExceptions.THERE_ARE_INCOMPATIBLE_SAMPLE_TYPES_WITH_THE_NEW_TYPE_OF_STUDY) {
                    const updateCaseAndDeleteIncompatibleSamples = await this.showConfirmationModalGlobal(this.$t('global.modal.case.incompatibleSamples'))
                    if (updateCaseAndDeleteIncompatibleSamples) {
                        this.updateLoading(true)
                        await this.updateCase(true)
                        this.$emit('samplesUpdated')
                        this.updateLoading(false)
                    }
                    return
                } else if (err.response && err.response.data && err.response.data.code === this.$apiExceptions.CONCURRENCY_ERROR) {
                    return this.processBasicConcurrencyConflict(this.$tc('global.case', 1), err, () => {
                        this.formData.originalConcurrencyUpdatedAt = err.response.data.currentConcurrencyUpdatedAt
                        return this.updateCase(deleteSamplesIfIncompatible)
                    })
                } else {
                    this.$_showErrorModalFromErrorResponse(err, this.$t('registry.index.saveFormError'))
                    return false
                }
            }
            this.initData(response)
            this.caseClone = cloneDeep(this.formData)
            this.$emit('mapCaseData', response)
            this.$emit('formUpdated')
            return true
        },
        validate() {
            return !!this.formData.physicianDepartment &&
              !!this.selectedPhysicianCenter &&
              !!this.formData.priority &&
              !!this.formData.caseType
        },
        hasCaseBeenChanged() {
            const caseClone = this.mapFormDataToRequest(this.caseClone)
            const formData = this.mapFormDataToRequest(this.formData)
            const hasCaseFormChanged = !isEqual(caseClone, formData)
            const hasClinicalDataChanged = this.hasClobChanged(this.caseClone, this.formData, 'clinicalData')
            const haveNotesChanged = this.hasClobChanged(this.caseClone, this.formData, 'notes')
            return !this.casId ||
                hasCaseFormChanged ||
                hasClinicalDataChanged ||
                haveNotesChanged ||
                !!(this.caseData && this.printedLabel !== this.caseData.isExternalLabel)
        },
        hasClobChanged(caseA, caseB, clobField) {
            if (!JSON.stringify(caseA[clobField]) && !JSON.stringify(caseB[clobField])) {
                return false
            }
            if (!JSON.stringify(caseA[clobField]) ^ !JSON.stringify(caseB[clobField])) {
                return true
            }
            return (caseA[clobField].text !== caseB[clobField].text)
        },
        onPathologistComboInput(pathologist) {
            this.formData.pathologist = pathologist
            this.formData.automaticPathologist = pathologist ? this.caseAutomaticPathologist?.id === pathologist?.id : false
        },
        changePathologist(pathologist, automaticChange) {
            this.formData.pathologist = pathologist
            this.formData.automaticPathologist = automaticChange
        },
        async loadPhysicianDepartments() {
            return this.loadDepartments(this.selectedPhysicianCenter)
        },
        async loadDestinationDepartments() {
            return this.loadDepartments(this.selectedDestinationCenter)
        },
        selectPhysicianCenter(selectedCenter) {
            this.formData.physicianDepartment = null
            this.selectedPhysicianCenterId = selectedCenter && selectedCenter.id
        },
        removePhysicianCenter() {
            this.formData.physicianDepartment = null
            this.selectedPhysicianCenterId = null
        },
        selectPhysicianDepartment(selectedDepartment) {
            this.formData.physicianDepartment = selectedDepartment
        },
        selectDestinationCenter(selectedCenter) {
            this.formData.destinationDepartment = null
            this.selectedDestinationCenterId = selectedCenter && selectedCenter.id
        },
        removeDestinationCenter() {
            this.formData.destinationDepartment = null
            this.selectedDestinationCenterId = null
        },
        selectDestinationDepartment(selectedDepartment) {
            this.formData.destinationDepartment = selectedDepartment
        },
        resetData() {
            this.selectedPhysicianCenter = this.defaultPhysicianCenter
            this.selectedPhysicianCenterId = this.defaultPhysicianCenterId
            this.selectedDestinationCenter = null
            this.selectedDestinationCenterId = null
            this.formData = this.getFormData()
            this.setFixFieldsContent()
            this.caseClone = cloneDeep(this.formData)
            this.casId = null
            this.caseLabel = ''
            this.destinationPet = null
            if (!this.fixFields) {
                this.$nextTick(() => this.$refs.caseTypeCombo.$refs.multiselect.activate())
            }
        }
    }
}

</script>

<style lang="scss">

    .registry-form .label-truncate {
        white-space: nowrap;
    }

    .registry-form .multiselect__tags {
        overflow: hidden !important;
    }

    .registry-form .form-box {
        padding: 0;
        margin: 0;
    }

    .registry-form .structure {
        box-shadow: none;
        border: 0;
    }

</style>

<style lang="scss" scoped>

    .separator {
        font-size: 1.3rem;
    }

    .mid-section-title {
        color: vt-color(primary);
        font-size: .9rem;
    }

    .text-box {
        height: 5.5rem;
    }

    .switch {
        position: relative;
        display: inline-block;
        width: 40px;
        height: 20px;
        background-color: rgba(0, 0, 0, 0.25);
        border-radius: 20px;
        transition: all 0.3s;
    }

    .switch::after {
        content: '';
        position: absolute;
        width: 18px;
        height: 18px;
        border-radius: 18px;
        background-color: white;
        top: 1px;
        left: 1px;
        transition: all 0.3s;
    }

    input[type='checkbox']:checked + .switch::after {
        transform: translateX(20px);
    }

    input[type='checkbox']:checked + .switch {
        background-color: vt-color(primary);
    }

    .offscreen {
        position: absolute;
        left: -9999px;
    }

    .description-btns i {
        font-size: 1.1rem !important;
    }

    .switch {
        position: relative;
        display: inline-block;
        width: 40px;
        height: 20px;
        background-color: rgba(0, 0, 0, 0.25);
        border-radius: 20px;
        transition: all 0.3s;
    }

    .switch::after {
        content: '';
        position: absolute;
        width: 18px;
        height: 18px;
        border-radius: 18px;
        background-color: white;
        top: 1px;
        left: 1px;
        transition: all 0.3s;
    }

    input[type='checkbox']:checked + .switch::after {
        transform: translateX(20px);
    }

    input[type='checkbox']:checked + .switch {
        background-color: vt-color(primary);
    }

    .offscreen {
        position: absolute;
        left: -9999px;
    }

    .description-btns i {
        font-size: 1.1rem !important;
    }

</style>
